import {
  SupplierRelationshipGetResponseType,
  SupplierRelationshipPaymentDueCalculateFromType,
  SupplierRelationshipPaymentTermsType,
} from "@sellar-io/api-v2/types";
import { DataButton, H4, H5, H6, Icon, IconType } from "@sellar-io/components";
import { ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { usePaymentTermsIndicator } from "../../../components/paymentTypeIndicator/usePaymentTermsIndicator";
import { Tooltip } from "@sellar-io/components";
import { TooltipTrigger } from "@sellar-io/components";
import { TooltipContent } from "@sellar-io/components";
import { AssignPriceListDialog } from "./AssignPriceListDialog";
import { useSupplierRelationshipUpdate } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipUpdate";
import { AssignProductCatalogueDialog } from "./AssignProductCatalogueDialog";
import { AssignPaymentTermsDialog } from "./AssignPaymentTermsDialog";
import { AssignDeliveryDialog } from "./AssignDeliveryDialog";

export const BusinessConnectionTradePills = ({
  connection,
}: {
  connection: SupplierRelationshipGetResponseType;
}) => {
  const [assignPaymentTermsDialogActive, setAssignPaymentTermsDialogActive] =
    useState(false);
  const [assignPriceListDialogActive, setAssignPriceListDialogActive] =
    useState(false);
  const [
    assignProductCatalogueDialogActive,
    setAssignProductCatalogueDialogActive,
  ] = useState(false);
  const [assignDeliveryDialogActive, setAssignDeliveryDialogActive] =
    useState(false);

  const deliveryMethods = () => {
    let deliveryMethods = "";

    const drayDeliveryMethod =
      connection.DrayDeliveryMethod ??
      connection.Relationship?.CustomerZone?.DrayDeliveryMethod ??
      null;
    const courierDeliveryMethod =
      connection.CourierDeliveryMethod ??
      connection.Relationship?.CustomerZone?.CourierDeliveryMethod ??
      null;
    const palletDeliveryMethod =
      connection.PalletDeliveryMethod ??
      connection.Relationship?.CustomerZone?.PalletDeliveryMethod ??
      null;

    const count = [
      drayDeliveryMethod,
      courierDeliveryMethod,
      palletDeliveryMethod,
    ].filter((x) => x != null).length;

    if (count === 0) return "Delivery from CZ";

    if (drayDeliveryMethod) {
      deliveryMethods = drayDeliveryMethod.name ?? "Direct";
    } else if (courierDeliveryMethod) {
      deliveryMethods = courierDeliveryMethod.name ?? "Courier delivery";
    } else if (palletDeliveryMethod) {
      deliveryMethods = palletDeliveryMethod.name ?? "Pallet delivery";
    }

    if (count > 0) {
      deliveryMethods = `${deliveryMethods} + ${count - 1} more`;
    }
    return deliveryMethods;
  };

  const paymentTerms = usePaymentTermsIndicator({
    connection: {
      ...connection,
      paymentDueCalculateFrom:
        connection.paymentDueCalculateFrom as SupplierRelationshipPaymentDueCalculateFromType,
      paymentTerms:
        connection.paymentTerms as SupplierRelationshipPaymentTermsType,
    },
    invoiceCreationStage: connection.Supplier.invoiceCreationStage,
  });

  const priceTier =
    connection.PriceTier ??
    connection.Relationship?.CustomerZone?.PriceTier ??
    null;

  const availabilityTier =
    connection.AvailabilityTier ??
    connection.Relationship?.CustomerZone?.AvailabilityTier ??
    null;

  const updateConnectionMutation = useSupplierRelationshipUpdate(connection.id);

  const loading = updateConnectionMutation.isPending;

  return (
    <>
      <div className="flex flex-col gap-2">
        <H4 className="flex items-center gap-2">
          Trade terms <TradeTermsInfo />
        </H4>
        <div className="flex flex-wrap gap-2">
          <TradeTermPill
            icon="MAP_MARKER"
            text={
              <>
                Customer zone:{" "}
                <span className="font-semibold">
                  {connection.Relationship?.CustomerZone?.name ?? "N/A"}
                </span>
              </>
            }
            isDirectlyAssigned={false}
          />
          <TradeTermPill
            icon="HANDSHAKE"
            text={paymentTerms.fullTextOnly || "N/A"}
            isDirectlyAssigned={false}
            onClick={() => setAssignPaymentTermsDialogActive(true)}
          />
          <TradeTermPill
            icon="TAG_OUTLINE"
            text={priceTier?.name || "N/A"}
            isDirectlyAssigned={connection.PriceTierId !== null}
            onClick={() => setAssignPriceListDialogActive(true)}
          />
          <TradeTermPill
            icon="BOOK_OUTLINE"
            text={availabilityTier?.name || "N/A"}
            isDirectlyAssigned={connection.AvailabilityTierId !== null}
            onClick={() => setAssignProductCatalogueDialogActive(true)}
          />
          <TradeTermPill
            icon="TRUCK_OUTLINE"
            text={deliveryMethods()}
            isDirectlyAssigned={
              !!connection.DrayDeliveryMethodId ||
              !!connection.CourierDeliveryMethodId ||
              !!connection.PalletDeliveryMethodId
            }
            onClick={() => setAssignDeliveryDialogActive(true)}
          />
        </div>
      </div>
      <AssignPaymentTermsDialog
        active={assignPaymentTermsDialogActive}
        setActive={setAssignPaymentTermsDialogActive}
        connection={connection}
      />
      <AssignPriceListDialog
        active={assignPriceListDialogActive}
        setActive={setAssignPriceListDialogActive}
        priceListId={connection.PriceTierId}
        loading={loading}
        onClick={(priceListId) => {
          updateConnectionMutation.mutate(
            {
              PriceTierId: priceListId,
              AvailabilityTierId: connection.AvailabilityTierId ?? null,
            },
            {
              onSuccess: () => {
                setAssignPriceListDialogActive(false);
              },
            },
          );
        }}
      />
      <AssignProductCatalogueDialog
        active={assignProductCatalogueDialogActive}
        setActive={setAssignProductCatalogueDialogActive}
        availabilityTierId={connection.AvailabilityTierId}
        loading={loading}
        onClick={(availabilityTierId) => {
          updateConnectionMutation.mutate(
            {
              PriceTierId: connection.PriceTierId ?? null,
              AvailabilityTierId: availabilityTierId,
            },
            {
              onSuccess: () => {
                setAssignProductCatalogueDialogActive(false);
              },
            },
          );
        }}
      />
      <AssignDeliveryDialog
        active={assignDeliveryDialogActive}
        setActive={setAssignDeliveryDialogActive}
        deliveryIds={connection}
        loading={loading}
        onClick={(deliveryMethodIds) => {
          updateConnectionMutation.mutate(deliveryMethodIds, {
            onSuccess: () => {
              setAssignDeliveryDialogActive(false);
            },
          });
        }}
      />
    </>
  );
};

const TradeTermsInfo = () => {
  return (
    <Tooltip placement="bottom">
      <TooltipTrigger className="w-fit cursor-pointer">
        <Icon icon={"INFORMATION_OUTLINE"} size={0.7} />
      </TooltipTrigger>
      <TooltipContent
        className={
          "Tooltip w-87.5 rounded-lg border border-zinc-300 bg-white px-4 py-3 shadow-lg"
        }
      >
        <div className="absolute -top-1.75 left-1/2 -translate-x-1/2">
          <div className="h-3 w-3 rotate-45 transform border-t border-l border-gray-300 bg-white"></div>
        </div>
        <div className="flex flex-row items-center gap-2">
          <Icon icon={"INFORMATION_OUTLINE"} className={"flex-none"} />
          <div className="flex flex-col">
            <H5>Configure customer trade terms</H5>
            <H6 className={"text-sm"}>
              Your customers assigned trade terms are listed. Any terms
              highlighted in <span className="text-blue-700">blue</span> are
              overriding the default terms inherited from their Customer Zone
            </H6>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const TradeTermPill = ({
  icon,
  text,
  isDirectlyAssigned,
  onClick,
}: {
  icon: IconType;
  text: ReactNode;
  isDirectlyAssigned: boolean;
  onClick?: () => void;
}) => {
  return (
    <DataButton
      border={true}
      onClick={onClick}
      className={twMerge(
        "flex items-center gap-2 font-normal",
        isDirectlyAssigned && "text-blue-700",
        !onClick && "pointer-events-none cursor-default",
      )}
    >
      <Icon icon={icon} size={0.7} />
      {text}
      {onClick && <Icon icon="PENCIL" size={0.7} />}
    </DataButton>
  );
};
